import React, {useEffect, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {Navbar} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {detailPages} from "../../constants";

export const menuItems = [
    {key: "My tournaments", href: "/admin/tournaments", icon: "home"},
    {key: "My clubs", href: "/admin/clubs", icon: "award"},
    {key: "Players", href: "/admin/bases", icon: "users"},
    {key: "Ratings", href: "/admin/ratings", icon: "bar-chart-2"},
    {key: "Locations", href: "/admin/locations", icon: "map-pin"}
];

export const publicMenuItems = [
    {key: "Tournaments", href: "/tournaments", icon: "calendar"},
    {key: "Clubs", href: "/clubs", icon: "award"},
    {key: "Players", href: "/players", icon: "users"},
    {key: "Locations", href: "/locations", icon: "map-pin"}
];

const modeRouteMap = {
    "/": "/admin/tournaments",
    "/tournaments": "/admin/tournaments",
    "/clubs": "/admin/clubs",
    "/players": "/admin/bases",
    "/locations": "/admin/locations",
    "/admin/tournaments": "/tournaments",
    "/admin/clubs": "/clubs",
    "/admin/bases": "/players",
    "/admin/locations": "/locations",
    "/admin/ratings": "/tournaments",
    "/admin/ratings/": "/tournaments"
};

export const getModeCounterpart = (pathname) => {
    if (modeRouteMap[pathname]) return modeRouteMap[pathname];
    // Nested admin routes (e.g. /admin/bases/:id/players) don't have their own
    // exact entry above - fall back to the counterpart of their admin section.
    const adminKey = Object.keys(modeRouteMap)
        .filter(key => key.startsWith("/admin/"))
        .find(key => pathname.startsWith(key.endsWith("/") ? key : key + "/"));
    return adminKey ? modeRouteMap[adminKey] : null;
};

export const matchHref = (location, href) => {
    const [hrefPath, hrefQueryStr] = href.split("?");
    if (hrefPath === "/") return location.pathname === "/" && !hrefQueryStr;
    const params = new URLSearchParams(location.search);
    if (hrefQueryStr) {
        if (location.pathname !== hrefPath) return false;
        const hrefParams = new URLSearchParams(hrefQueryStr);
        for (const [key, value] of hrefParams.entries()) {
            if (params.get(key) !== value) return false;
        }
        return true;
    }
    if (location.pathname === hrefPath || location.pathname.startsWith(hrefPath + "/")) {
        return !params.get("view");
    }
    return false;
};

// the top-level list pages (Tournaments/Clubs/Players/Ratings/Locations, in
// either their public or /admin variant) always have a proper translated
// label available right here via menuItems/publicMenuItems - resolving the
// header title from the current path this way, fresh on every render,
// avoids both raw untranslated URL segments (t("clubs") has no matching
// key - only t("Clubs") does) and the "admin" segment winning for admin
// routes (pathSegments[0] there is "admin", not the actual section).
const allMenuItems = [...menuItems, ...publicMenuItems];
const titleForPath = (location, t) => {
    const item = allMenuItems.find(i => matchHref(location, i.href));
    return item ? t(item.key) : null;
};

export default function MainHeader({ user, logoutUser, rightControls }) {
    const location = useLocation();
    const { t } = useTranslation();

    const pathSegments = location.pathname.split("/").filter(Boolean);
    // detail pages (a tournament, a club/player profile, etc.) aren't in
    // the menu list above - they set their own title via localStorage
    // (see PlayersList.js) since it depends on data only they have (a
    // tournament/player name), not just the route.
    const [pageTitle, setPageTitle] = useState(localStorage.getItem("pageTitle") || t(pathSegments[0]));

    useEffect(() => {
        const handleStorageChange = () => setPageTitle(localStorage.getItem("pageTitle") || t(pathSegments[0]));
        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    const headerTitle = titleForPath(location, t) || pageTitle;

    useEffect(() => {
        const pathSegments = location.pathname.split("/").filter(Boolean);
        const isDetailPage = detailPages.includes(pathSegments[0]) && pathSegments.length > 1;
        document.title = !isDetailPage ? (titleForPath(location, t) || t("Tournament Platform")) : pageTitle;
    }, [location, t, pageTitle]);

    return (
        <Navbar>
            <nav className="main-menu">
                <ul>
                    <li><Link to={"/tournaments"}>LT</Link></li>
                    <li className="main-menu-page-title">{headerTitle}</li>
                </ul>
            </nav>

            {rightControls}
        </Navbar>
    );
}
